import { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LiquidityCapTableByStakeholder } from '../../../../../types/scenario-modeling.types';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { CapTableTab, Column, StakeholderColumns, StakeholdersViewBy } from '../liquidity.types';
import { FirstTableRowPayload, SecondTableRowPayload, TableRow } from '../table-components/index';

export type StakeholderTableItemProps = {
  className?: string;
  fields: Record<StakeholderColumns, FirstTableRowPayload | null | SecondTableRowPayload>;
  items?: LiquidityCapTableByStakeholder[];
  modifiedShareClassesIds?: string[];
  possibleShareClasses: string[];
  selectedViewBy: 'all' | StakeholdersViewBy;
  trClassName?: string;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export const StakeholderTableItem: FC<StakeholderTableItemProps> = ({
  fields,
  visibleColumns,
  className,
  trClassName,
  viewBy,
  selectedViewBy,
  items = [],
  possibleShareClasses,
  modifiedShareClassesIds = [],
}) => {
  const [isExpanded, setExpanded] = useState(false);

  const isItemsEmpty = items.length === 0;

  return (
    <>
      <TableRow
        fields={fields}
        handleExpandRow={setExpanded}
        isExpanded={isExpanded}
        isExpandedRow={isItemsEmpty}
        selectedViewBy={selectedViewBy}
        tab={CapTableTab.BY_STAKEHOLDER}
        tdClassName={className}
        trClassName={twMerge(trClassName, isExpanded && 'bg-brand-25')}
        viewBy={viewBy}
        visibleColumns={visibleColumns}
      />
      {isExpanded &&
        items.map(
          (
            {
              avgSharePrice,
              diluted,
              dilutedPercentage,
              investment,
              issued,
              issuedPercentage,
              votingPercentage,
              shareClasses,
              name,
              avgSharePriceChange,
              dilutedChange,
              dilutedPercentageChange,
              investmentChange,
              issuedChange,
              issuedPercentageChange,
              voting,
              votingChange,
              votingPercentageChange,
              effectiveLiquidityProceeds,
              minLiquidityProceeds,
              participatingOnProceeds,
              proceedsWithoutLiquidity,
              roi,
              commonStockProceeds,
              seniorityColumns,
            },
            i,
          ) => {
            const defaultShareClass = shareClasses.find(({ isDefault }) => isDefault);
            const otherShareClass = shareClasses.filter(
              ({ isDefault, name }) => !isDefault && possibleShareClasses.includes(name),
            );
            const otherShareClassWithIssued = otherShareClass.filter(({ issued }) => issued > 0);

            const hasModified = otherShareClassWithIssued.some(({ shareClass }) =>
              modifiedShareClassesIds.includes(shareClass?.id),
            );

            return (
              <TableRow
                className="bg-[#F9FAFB] shadow-[inset_2px_0px_0px_0px_#2565C8]"
                fields={{
                  [Column.NAME]: { value: name, change: null },
                  [Column.STAKEHOLDER_NAME]: { value: name, change: null },
                  [Column.TYPE]: {
                    value: '-',
                    change: null,
                  },
                  [Column.ISSUED]: {
                    change: issuedPercentageChange,
                    value: issuedPercentage * 100,
                  },
                  [Column.DILUTED]: {
                    value: dilutedPercentage * 100,
                    change: dilutedPercentageChange,
                  },
                  [Column.VOTING]: {
                    value: votingPercentage * 100,
                    change: votingPercentageChange,
                  },

                  [Column.NO_ISSUED]: {
                    change: issuedChange,
                    value: issued,
                  },
                  [Column.DILUTED_SHARES]: {
                    change: dilutedChange,
                    value: diluted,
                  },
                  [Column.COMMON_STOCK]: {
                    change: toNumber(defaultShareClass?.issuedChange),
                    value: toNumber(defaultShareClass?.issued),
                  },
                  [Column.OTHER_SHARE_CLASSES]: {
                    value: otherShareClass.map(({ issued }) => issued),
                    change: otherShareClass.map(({ issuedChange }) => issuedChange),
                  },
                  [Column.VOTING_RIGHTS]: {
                    change: votingChange,
                    value: voting,
                  },
                  [Column.INVESTMENT]: {
                    change: investmentChange,
                    value: investment,
                  },
                  [Column.AVERAGE_SHARE_PRICE]: {
                    change: avgSharePriceChange,
                    value: avgSharePrice,
                  },
                  [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: {
                    change: null,
                    value: proceedsWithoutLiquidity,
                  },
                  [Column.MIN_LIQUIDITY_PROCEEDS]: { change: null, value: minLiquidityProceeds },
                  [Column.PARTICIPATING_ON_PROCEEDS]: {
                    change: null,
                    value: toMathRound(participatingOnProceeds, 0),
                  },
                  [Column.COMMON_STOCK_PROCEEDS]: { value: commonStockProceeds, change: null },
                  [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: {
                    value: effectiveLiquidityProceeds,
                    change: null,
                  },
                  [Column.OTHER_SHARE_CLASSES_PROCEEDS]: otherShareClass.map(
                    ({ capParticipating, investmentDays, multiple, investment }) => ({
                      cap: capParticipating,
                      investment,
                      investmentTime: investmentDays,
                      multiple,
                    }),
                  ),
                  [Column.SENIORITY]: {
                    change: null,
                    value: seniorityColumns.map(({ seniorityValue }) => seniorityValue),
                  },
                  [Column.ROI]: { change: null, value: roi },
                }}
                isExpandedRow={true}
                key={`${i}_${name}`}
                selectedViewBy={selectedViewBy}
                showToolTip={hasModified}
                tab={CapTableTab.BY_STAKEHOLDER}
                trClassName="bg-brand-25"
                viewBy={viewBy}
                visibleColumns={visibleColumns}
              />
            );
          },
        )}
    </>
  );
};
